import { useEffect, useMemo, useRef, useState } from 'react'
import { useSession } from '../context/SessionContext'
import { useAsync } from '../hooks/useAsync'
import { errorMessage } from '../services/api'
import {
  addSeasonSignup,
  createLeaguePlayer,
  createManualGame,
  deleteChampion,
  fetchAdminGames,
  fetchAdminUsers,
  fetchChampions,
  fetchLeaguePlayers,
  fetchSaveStateInventory,
  fetchSeasonSignups,
  fetchSeasons,
  fetchSignupStatus,
  fetchUploadLog,
  importSeasonExcel,
  linkLeaguePlayer,
  makeSeasonCurrent,
  proposeConferenceSplit,
  removeSeasonSignup,
  downloadGameSaveState,
  reparseAllSaveStates,
  reparseGame,
  resyncRosterNames,
  saveChampion,
  saveSeasonMemberships,
  setGameStatus,
  setLeagueDisplayName,
  setSeasonDeadlines,
  setSeasonStatus,
  setUserAdmin,
  updateGameResult,
  type GameResultPayload,
  type ReparseOutcome,
  type ReparseSummary,
  type ResyncSummary,
} from '../services/league'
import {
  Avatar,
  Card,
  ConferenceBadge,
  EmptyState,
  Loading,
  Notice,
  PlayerCell,
  PlayoffBadge,
  SEASON_STATUSES,
  Segmented,
  SortableHeaderRow,
  Stat,
  dateSortValue,
  formatCalendarDate,
  formatDate,
  formatSeasonStatus,
  seasonRecordsPlayoffs,
  useTableSort,
  type SortableColumn,
} from '../components/ui'
import PlayerAliasPanel from '../components/PlayerAliasPanel'
import PlayerMergePanel from '../components/PlayerMergePanel'
import PlayoffsPanel from '../components/PlayoffsPanel'
import PollPanel from '../components/PollPanel'
import UpdatesPanel from '../components/UpdatesPanel'
import { TeamSelect } from '../components/TeamSelect'
import type {
  AdminUser,
  Champion,
  Conference,
  ConferenceDraftProposal,
  GameSummary,
  ImportSummary,
  LeaguePlayer,
  Season,
  SeasonSignup,
  SeasonStatus,
  UploadLogEntry,
} from '../types'
import './Admin.css'

type Tab =
  | 'users'
  | 'merge'
  | 'aliases'
  | 'signups'
  | 'conferences'
  | 'games'
  | 'playoffs'
  | 'poll'
  | 'updates'
  | 'champions'
  | 'import'

// Signups sit immediately before Conferences because that is the order the two
// happen in: the pool is gathered, then it is split into rosters. Playoffs then
// follow Games for the same reason, and sit beside the Hall of Champions a
// finished title series writes on its own.
const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'users', label: 'Users & players' },
  { key: 'merge', label: 'Merge players' },
  { key: 'aliases', label: 'Import aliases' },
  { key: 'signups', label: 'Season signups' },
  { key: 'conferences', label: 'Conferences' },
  { key: 'games', label: 'Games' },
  { key: 'playoffs', label: 'Playoffs' },
  { key: 'poll', label: 'Poll' },
  { key: 'updates', label: 'Updates' },
  { key: 'champions', label: 'Hall of Champions' },
  { key: 'import', label: 'Historical import' },
]

// --- Users -----------------------------------------------------------------

/**
 * One editable league name.
 *
 * @param player - The player as the server currently resolves them.
 * @param onSaved - Called after a successful write so the parent can reload.
 *
 * @remarks
 * The draft is seeded from the resolved name rather than from the override, so
 * the field always shows what the site shows and the owner edits what he sees.
 * Saving that unchanged draft on a player with no override would create one that
 * merely freezes today's Discord name, so Save stays disabled until the text
 * actually differs from what is already rendered.
 */
function LeagueNameRow({
  player,
  onSaved,
  onError,
}: {
  player: LeaguePlayer
  onSaved: () => void
  onError: (message: string | null) => void
}) {
  const [draft, setDraft] = useState(player.displayName)
  const [busy, setBusy] = useState(false)

  // A reload hands back a new object for the same row; follow the server rather
  // than keeping a stale draft on screen.
  useEffect(() => {
    setDraft(player.displayName)
  }, [player.displayName])

  const trimmed = draft.trim()
  const canSave = Boolean(trimmed) && trimmed !== player.displayName
  const overridden = player.leagueDisplayName !== null

  async function apply(value: string | null): Promise<void> {
    setBusy(true)
    onError(null)
    try {
      await setLeagueDisplayName(player.id, value)
      onSaved()
    } catch (caught) {
      onError(errorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  return (
    <tr>
      <td>
        <PlayerCell
          name={player.displayName}
          discordId={player.discordId}
          avatar={player.discordAvatar}
        />
      </td>
      <td className="muted">
        {player.userId === null ? (
          <span className="badge">No Discord account</span>
        ) : (
          player.discordDisplayName
        )}
      </td>
      <td>
        {overridden ? (
          <span className="badge badge--afc">League name</span>
        ) : (
          <span className="badge">Follows Discord</span>
        )}
      </td>
      <td>
        <div className="row">
          <input
            className="input"
            style={{ maxWidth: 200 }}
            value={draft}
            maxLength={32}
            disabled={busy}
            onChange={event => setDraft(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter' && canSave) void apply(trimmed)
            }}
          />
          <button
            type="button"
            className="btn btn--sm"
            disabled={busy || !canSave}
            onClick={() => void apply(trimmed)}
          >
            Save
          </button>
          <button
            type="button"
            className="btn btn--danger btn--sm"
            disabled={busy || !overridden}
            title={
              overridden
                ? `Clear the override and show "${player.discordDisplayName}" again`
                : 'No override to clear'
            }
            onClick={() => void apply(null)}
          >
            Clear
          </button>
        </div>
      </td>
    </tr>
  )
}

const ACCOUNT_COLUMNS: Array<SortableColumn<AdminUser>> = [
  { key: 'member', label: 'Member', sort: user => user.displayName },
  {
    key: 'lastLogin',
    label: 'Last login',
    hint: 'Sort by when each account last signed in',
    sort: user => dateSortValue(user.lastLoginAt),
  },
  { key: 'role', label: 'Role', sort: user => (user.isAdmin ? 'Admin' : 'Member') },
  { key: 'actions' },
]

/**
 * Every Discord account, with the admin toggle.
 *
 * @remarks
 * Sortable even though each row carries a button, because the button is a
 * per-row action rather than a field being filled in: nothing is half-typed
 * when the order changes. Sorting by last login is the fastest way to find the
 * account that just signed in and needs promoting.
 */
function AccountsTable({
  users,
  onToggleAdmin,
}: {
  users: AdminUser[]
  onToggleAdmin: (userId: number, isAdmin: boolean) => void
}) {
  const sort = useTableSort(users, ACCOUNT_COLUMNS)

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <SortableHeaderRow columns={ACCOUNT_COLUMNS} sort={sort} />
        </thead>
        <tbody>
          {sort.rows.map(user => (
            <tr key={user.id}>
              <td>
                <PlayerCell
                  name={user.displayName}
                  discordId={user.discordId}
                  avatar={user.discordAvatar}
                />
              </td>
              <td className="muted">{formatDate(user.lastLoginAt)}</td>
              <td>
                {user.isAdmin ? (
                  <span className="badge badge--afc">Admin</span>
                ) : (
                  <span className="badge">Member</span>
                )}
              </td>
              <td className="num">
                <button
                  type="button"
                  className={user.isAdmin ? 'btn btn--danger btn--sm' : 'btn btn--sm'}
                  onClick={() => onToggleAdmin(user.id, !user.isAdmin)}
                >
                  {user.isAdmin ? 'Revoke admin' : 'Make admin'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function UsersPanel() {
  const users = useAsync(fetchAdminUsers, [])
  const players = useAsync(fetchLeaguePlayers, [])
  const [error, setError] = useState<string | null>(null)
  const [newPlayerName, setNewPlayerName] = useState('')

  async function toggleAdmin(userId: number, isAdmin: boolean): Promise<void> {
    setError(null)
    try {
      await setUserAdmin(userId, isAdmin)
      users.reload()
    } catch (caught) {
      setError(errorMessage(caught))
    }
  }

  async function addPlayer(): Promise<void> {
    if (!newPlayerName.trim()) return
    setError(null)
    try {
      await createLeaguePlayer(newPlayerName.trim())
      setNewPlayerName('')
      players.reload()
    } catch (caught) {
      setError(errorMessage(caught))
    }
  }

  async function link(leaguePlayerId: number, userId: number | null): Promise<void> {
    setError(null)
    try {
      await linkLeaguePlayer(leaguePlayerId, userId)
      players.reload()
      users.reload()
    } catch (caught) {
      setError(errorMessage(caught))
    }
  }

  const unlinked = (players.data ?? []).filter(player => player.userId === null)
  const roster = players.data ?? []

  return (
    <div className="stack">
      {error && <Notice tone="error">{error}</Notice>}

      {/*
        Not sortable, and not an oversight: every row owns a text input whose
        draft lives in that row's component state. Re-ordering the table under
        an admin who is halfway through renaming somebody moves the field out
        from under the cursor, and the payoff — a second alphabetisation of a
        list the server already returns in a stable order — is not worth it.
      */}
      <Card
        title="League display names"
        subtitle="What the site calls each player. Blank the field is not an option — use Clear to fall back to Discord."
        flush
      >
        {players.loading ? (
          <div style={{ padding: 24 }}>
            <Loading rows={6} />
          </div>
        ) : roster.length === 0 ? (
          <div style={{ padding: 24 }}>
            <EmptyState title="No player identities yet" />
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Shown on the site</th>
                  <th>Discord name</th>
                  <th>Source</th>
                  <th>League name</th>
                </tr>
              </thead>
              <tbody>
                {roster.map(player => (
                  <LeagueNameRow
                    key={player.id}
                    player={player}
                    onSaved={() => players.reload()}
                    onError={setError}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Discord accounts" subtitle="Grant or revoke admin access" flush>
        {users.loading ? (
          <div style={{ padding: 24 }}>
            <Loading rows={4} />
          </div>
        ) : (
          <AccountsTable
            users={users.data ?? []}
            onToggleAdmin={(userId, isAdmin) => void toggleAdmin(userId, isAdmin)}
          />
        )}
      </Card>

      <Card
        title="Unlinked player identities"
        subtitle="Names created by historical imports that have no Discord account yet"
      >
        <div className="stack-sm">
          <div className="row">
            <input
              className="input"
              style={{ maxWidth: 260 }}
              placeholder="Add a player by name"
              value={newPlayerName}
              onChange={event => setNewPlayerName(event.target.value)}
            />
            <button type="button" className="btn" onClick={() => void addPlayer()}>
              Add player
            </button>
          </div>

          {/*
            Two columns, one of which is a <select> the admin is operating.
            Sorting a name column that is already the only text here would move
            rows mid-decision for no gain.
          */}
          {unlinked.length === 0 ? (
            <EmptyState title="Every player identity is linked to an account" />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Player</th>
                    <th>Link to account</th>
                  </tr>
                </thead>
                <tbody>
                  {unlinked.map(player => (
                    <tr key={player.id}>
                      <td>{player.displayName}</td>
                      <td>
                        <select
                          className="select"
                          style={{ maxWidth: 260 }}
                          defaultValue=""
                          onChange={event =>
                            void link(
                              player.id,
                              event.target.value ? Number(event.target.value) : null
                            )
                          }
                        >
                          <option value="">Not linked</option>
                          {users.data?.map(user => (
                            <option key={user.id} value={user.id}>
                              {user.displayName}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}

// --- Season signups --------------------------------------------------------

const SIGNUP_COLUMNS: Array<SortableColumn<SeasonSignup>> = [
  { key: 'player', label: 'Player', sort: row => row.displayName },
  {
    key: 'conference',
    label: 'Conference',
    hint: 'AFC, then NFC, then everyone still to be placed',
    sort: row => row.conference ?? 'Unassigned',
  },
  {
    key: 'source',
    label: 'How',
    sortLabel: 'who entered them',
    sort: row => row.source,
  },
  { key: 'when', label: 'Signed up', sort: row => dateSortValue(row.signedUpAt) },
  { key: 'remove', label: '' },
]

/**
 * The signup pool for one season, with the roster it feeds.
 *
 * @param signups - Everyone who has put their hand up, in name order.
 * @param onRemove - Drops one player; the row's own button is disabled while
 * any write is in flight, since a second click during the first would race.
 *
 * @remarks
 * The Conference column is the point of this table rather than a decoration.
 * Signing up and being rostered are separate steps, and the players showing
 * `Unassigned` are precisely the ones the Conferences tab still has to place.
 */
function SignupsTable({
  signups,
  busy,
  onRemove,
}: {
  signups: SeasonSignup[]
  busy: boolean
  onRemove: (leaguePlayerId: number) => void
}) {
  const sort = useTableSort(signups, SIGNUP_COLUMNS)

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <SortableHeaderRow columns={SIGNUP_COLUMNS} sort={sort} />
        </thead>
        <tbody>
          {sort.rows.map(row => (
            <tr key={row.leaguePlayerId}>
              <td>
                <PlayerCell
                  name={row.displayName}
                  discordId={row.discordId}
                  avatar={row.discordAvatar}
                />
              </td>
              <td>
                <ConferenceBadge conference={row.conference} />
              </td>
              <td className="muted">
                {row.source === 'self'
                  ? 'Signed up themselves'
                  : `Added by ${row.addedBy ?? 'an admin'}`}
              </td>
              <td className="muted">{formatDate(row.signedUpAt)}</td>
              <td>
                <button
                  type="button"
                  className="btn btn--danger btn--sm"
                  disabled={busy}
                  onClick={() => onRemove(row.leaguePlayerId)}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Who has volunteered for a season, and hand edits to that list.
 *
 * @param onOpenConferences - Switches to the tab that turns this pool into
 * AFC/NFC rosters, since that is the next thing to do with a full list.
 *
 * @remarks
 * Opens on the season being gathered for, or on the current season once public
 * signups have closed — so a late add after kickoff lands on the roster in
 * play rather than on next year's empty pool.
 */
function SignupsPanel({
  onOpenConferences,
}: {
  onOpenConferences: (seasonNumber: number) => void
}) {
  const seasons = useAsync(fetchSeasons, [])
  const players = useAsync(fetchLeaguePlayers, [])
  const upcoming = useAsync(fetchSignupStatus, [])
  const [seasonNumber, setSeasonNumber] = useState<number | null>(null)
  const [status, setStatus] = useState<{ tone: 'success' | 'error'; text: string } | null>(null)
  const [choice, setChoice] = useState('')
  const [conference, setConference] = useState<Conference | ''>('')
  const [busy, setBusy] = useState(false)

  const openNumber = upcoming.data?.number ?? null
  const currentNumber = seasons.data?.find(season => season.isCurrent)?.number ?? null
  const defaultSeason =
    upcoming.data?.open === true ? openNumber : (currentNumber ?? openNumber)
  const activeSeason = seasonNumber ?? defaultSeason

  const signups = useAsync(
    () => (activeSeason === null ? Promise.resolve([]) : fetchSeasonSignups(activeSeason)),
    [activeSeason]
  )

  // The season signups are open for has no row until somebody joins it, so it
  // is offered alongside the seasons that do exist rather than read off them.
  const seasonNumbers = useMemo(() => {
    const numbers = new Set((seasons.data ?? []).map(season => season.number))
    if (openNumber !== null) {
      numbers.add(openNumber)
    }
    return [...numbers].sort((a, b) => b - a)
  }, [seasons.data, openNumber])

  const rows = signups.data ?? []
  const signedUpIds = new Set(rows.map(row => row.leaguePlayerId))
  const addable = (players.data ?? []).filter(player => !signedUpIds.has(player.id))
  const unplaced = rows.filter(row => row.conference === null).length
  const selectedSeason = seasons.data?.find(season => season.number === activeSeason)
  const seasonUnderway =
    selectedSeason?.status === 'active' || selectedSeason?.status === 'postseason'

  async function run(action: () => Promise<unknown>, success: string): Promise<void> {
    setBusy(true)
    setStatus(null)

    try {
      await action()
      signups.reload()
      setStatus({ tone: 'success', text: success })
    } catch (caught) {
      setStatus({ tone: 'error', text: errorMessage(caught) })
    } finally {
      setBusy(false)
    }
  }

  async function add(): Promise<void> {
    if (activeSeason === null || !choice) return
    if (seasonUnderway && conference === '') return

    const leaguePlayerId = Number(choice)
    const name = addable.find(player => player.id === leaguePlayerId)?.displayName ?? 'Player'
    const placed = conference === '' ? undefined : conference
    setChoice('')
    setConference('')
    await run(
      () => addSeasonSignup(activeSeason, leaguePlayerId, placed),
      placed
        ? `${name} is in the ${placed} for season ${activeSeason}.`
        : `${name} is signed up for season ${activeSeason}.`
    )
  }

  async function remove(leaguePlayerId: number): Promise<void> {
    if (activeSeason === null) return

    const name = rows.find(row => row.leaguePlayerId === leaguePlayerId)?.displayName ?? 'Player'
    await run(
      () => removeSeasonSignup(activeSeason, leaguePlayerId),
      `${name} is no longer signed up for season ${activeSeason}.`
    )
  }

  const seasonPicker = (
    <select
      className="select"
      style={{ minWidth: 200 }}
      value={activeSeason ?? ''}
      onChange={event => setSeasonNumber(Number(event.target.value))}
    >
      {seasonNumbers.map(number => (
        <option key={number} value={number}>
          Season {number}
          {upcoming.data?.open && number === openNumber ? ' · signups open' : ''}
        </option>
      ))}
    </select>
  )

  return (
    <div className="stack">
      {status && <Notice tone={status.tone}>{status.text}</Notice>}

      <Card
        title="Season signups"
        subtitle={
          seasonUnderway
            ? 'Public signups are closed. Add a player here with a conference to put them on the roster and in the round robin.'
            : 'Who has put their hand up for a season. This is the pool conferences get drawn from, not the roster itself.'
        }
        actions={seasonPicker}
      >
        <div className="stack">
          <div className="grid-3">
            <Stat
              label="Signed up"
              value={signups.loading ? '—' : rows.length}
              hint={activeSeason === null ? undefined : `Season ${activeSeason}`}
            />
            <Stat
              label="Awaiting a conference"
              value={signups.loading ? '—' : unplaced}
              hint="Signed up but not yet on an AFC or NFC roster"
            />
          </div>

          <div className="row">
            <select
              className="select"
              style={{ maxWidth: 280 }}
              value={choice}
              onChange={event => setChoice(event.target.value)}
            >
              <option value="">Add a player by hand…</option>
              {addable.map(player => (
                <option key={player.id} value={player.id}>
                  {player.displayName}
                  {player.userId === null ? ' (no Discord account)' : ''}
                </option>
              ))}
            </select>
            <select
              className="select"
              style={{ maxWidth: 180 }}
              value={conference}
              onChange={event => setConference(event.target.value as Conference | '')}
            >
              <option value="">
                {seasonUnderway ? 'Conference…' : 'Conference (optional)…'}
              </option>
              <option value="AFC">AFC</option>
              <option value="NFC">NFC</option>
            </select>
            <button
              type="button"
              className="btn btn--primary"
              disabled={
                !choice ||
                busy ||
                activeSeason === null ||
                (seasonUnderway && conference === '')
              }
              onClick={() => void add()}
            >
              {busy ? 'Saving…' : seasonUnderway ? 'Add to season' : 'Add to signups'}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={activeSeason === null || rows.length < 2 || busy}
              onClick={() => activeSeason !== null && onOpenConferences(activeSeason)}
            >
              Assign conferences
            </button>
          </div>

          <span className="muted" style={{ fontSize: '0.82rem' }}>
            Anyone on record can be added, including the imported names with no Discord account.
            {seasonUnderway
              ? ' A conference is required after kickoff so they appear on standings and everyone else in that conference has them on the schedule.'
              : ' Removing a signup leaves any conference assignment they already have alone.'}
          </span>
        </div>
      </Card>

      <Card title="Signup list" subtitle={`Season ${activeSeason ?? '—'}`} flush>
        {signups.loading ? (
          <div style={{ padding: 24 }}>
            <Loading rows={5} />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState title="Nobody has signed up yet">
            Players sign themselves up from the button on the home page, or add them here.
          </EmptyState>
        ) : (
          <SignupsTable signups={rows} busy={busy} onRemove={id => void remove(id)} />
        )}
      </Card>
    </div>
  )
}

// --- Conferences -----------------------------------------------------------

const SEASON_STATUS_OPTIONS = SEASON_STATUSES.map(value => ({
  value,
  label: formatSeasonStatus(value),
}))

/** The stored deadlines as the three text fields hold them; blank means unset. */
function toDeadlineDraft(season: Season) {
  return {
    midseasonDeadline: season.midseasonDeadline ?? '',
    endOfSeasonDeadline: season.endOfSeasonDeadline ?? '',
    midseasonRequiredGames:
      season.midseasonRequiredGames === null ? '' : String(season.midseasonRequiredGames),
  }
}

/**
 * The two dates one season holds its players to.
 *
 * @param season - Season being configured; the fields follow it, so the picker
 * above switches which season is being edited.
 * @param onSaved - Called after a successful write so the parent can reload.
 *
 * @remarks
 * Leaving a field blank means that deadline does not exist, which is where
 * every season starts and where one set by mistake should be returned to:
 * clearing a date un-flags everyone it caught, because delinquency is worked
 * out when the standings are read rather than recorded against a player.
 *
 * Consequences are spelled out under the fields on purpose. Saving a past date
 * here can reorder a live conference table and paint names red without a game
 * being played, which is not something to discover afterwards.
 */
function SeasonDeadlineForm({
  season,
  onSaved,
  onError,
}: {
  season: Season
  onSaved: (message: string) => void
  onError: (message: string) => void
}) {
  const [draft, setDraft] = useState(() => toDeadlineDraft(season))
  const [saving, setSaving] = useState(false)

  const stored = toDeadlineDraft(season)
  useEffect(() => {
    setDraft(toDeadlineDraft(season))
    // Following the stored values rather than the object identity: the seasons
    // list is refetched after every save and hands back a fresh object each time.
  }, [
    season.number,
    season.midseasonDeadline,
    season.endOfSeasonDeadline,
    season.midseasonRequiredGames,
  ])

  const dirty =
    draft.midseasonDeadline !== stored.midseasonDeadline ||
    draft.endOfSeasonDeadline !== stored.endOfSeasonDeadline ||
    draft.midseasonRequiredGames !== stored.midseasonRequiredGames

  async function save(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    setSaving(true)

    try {
      await setSeasonDeadlines(season.number, {
        midseasonDeadline: draft.midseasonDeadline || null,
        endOfSeasonDeadline: draft.endOfSeasonDeadline || null,
        midseasonRequiredGames: draft.midseasonRequiredGames
          ? Number(draft.midseasonRequiredGames)
          : null,
      })
      onSaved(`Season ${season.number} deadlines saved.`)
    } catch (caught) {
      onError(errorMessage(caught))
    } finally {
      setSaving(false)
    }
  }

  return (
    <form className="admin-form" onSubmit={event => void save(event)}>
      <label className="field">
        <span className="field__label">Midseason deadline</span>
        <input
          className="input"
          type="date"
          value={draft.midseasonDeadline}
          onChange={event =>
            setDraft(current => ({ ...current, midseasonDeadline: event.target.value }))
          }
        />
        <span className="field__hint">Blank for none · runs to midnight CT</span>
      </label>

      <label className="field">
        <span className="field__label">Games required by then</span>
        <input
          className="input"
          type="number"
          min={0}
          value={draft.midseasonRequiredGames}
          placeholder="e.g. 6"
          onChange={event =>
            setDraft(current => ({ ...current, midseasonRequiredGames: event.target.value }))
          }
        />
        <span className="field__hint">Counted from when games were played</span>
      </label>

      <label className="field">
        <span className="field__label">End of season deadline</span>
        <input
          className="input"
          type="date"
          value={draft.endOfSeasonDeadline}
          onChange={event =>
            setDraft(current => ({ ...current, endOfSeasonDeadline: event.target.value }))
          }
        />
        <span className="field__hint">All regular season games in by midnight CT</span>
      </label>

      <div className="admin-form__wide row">
        <button type="submit" className="btn btn--primary" disabled={!dirty || saving}>
          {saving ? 'Saving…' : 'Save deadlines'}
        </button>
        <span className="muted" style={{ fontSize: '0.82rem' }}>
          Once a deadline has passed, a player who was short of it is shown in red in the
          standings and loses every tiebreaker.
        </span>
      </div>
    </form>
  )
}

function ConferenceSplitModal({
  seasonNumber,
  draft,
  saving,
  onClose,
  onSave,
}: {
  seasonNumber: number | null
  draft: ConferenceDraftProposal
  saving: boolean
  onClose: () => void
  onSave: () => void
}) {
  const afc = draft.assignments
    .filter(row => row.conference === 'AFC')
    .sort((a, b) => a.displayName.localeCompare(b.displayName))
  const nfc = draft.assignments
    .filter(row => row.conference === 'NFC')
    .sort((a, b) => a.displayName.localeCompare(b.displayName))
  const finalists = new Set(
    draft.assignments.filter(row => row.tier === 0).map(row => row.leaguePlayerId)
  )

  return (
    <div
      className="admin-modal-backdrop"
      onClick={event => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        className="admin-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="conference-split-title"
      >
        <header className="admin-modal__header">
          <div>
            <h2 id="conference-split-title" className="admin-modal__title">
              Proposed split{seasonNumber !== null ? ` — Season ${seasonNumber}` : ''}
            </h2>
            <p className="muted" style={{ margin: '4px 0 0', fontSize: '0.82rem' }}>
              AFC {afc.length} · NFC {nfc.length}
              {draft.needsManual.length > 0
                ? ` · ${draft.needsManual.length} to place by hand`
                : ''}
              . Not saved yet.
            </p>
          </div>
          <button type="button" className="btn btn--sm" onClick={onClose}>
            Close
          </button>
        </header>

        {draft.championship && (
          <p className="muted" style={{ fontSize: '0.85rem' }}>
            Season {draft.championship.previousSeason} championship:{' '}
            {draft.championship.championName} vs {draft.championship.runnerUpName}
            {draft.championship.note ? `. ${draft.championship.note}` : ', split into opposite conferences.'}
          </p>
        )}

        <div className="admin-modal__split">
          <section>
            <h3 className="admin-modal__conference admin-modal__conference--afc">AFC</h3>
            <ul className="admin-modal__list">
              {afc.map(row => (
                <li key={row.leaguePlayerId}>
                  {row.displayName}
                  {finalists.has(row.leaguePlayerId) ? (
                    <span className="admin-modal__tag">Finalist</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h3 className="admin-modal__conference admin-modal__conference--nfc">NFC</h3>
            <ul className="admin-modal__list">
              {nfc.map(row => (
                <li key={row.leaguePlayerId}>
                  {row.displayName}
                  {finalists.has(row.leaguePlayerId) ? (
                    <span className="admin-modal__tag">Finalist</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        </div>

        {draft.needsManual.length > 0 && (
          <section>
            <h3 className="admin-modal__conference">Place by hand</h3>
            <ul className="admin-modal__list">
              {draft.needsManual.map(row => (
                <li key={row.leaguePlayerId}>
                  {row.displayName}
                  <span className="muted"> — {row.reason}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn" onClick={onClose}>
            Keep editing
          </button>
          <button type="button" className="btn btn--primary" disabled={saving} onClick={onSave}>
            {saving ? 'Saving…' : 'Save assignments'}
          </button>
        </div>
      </div>
    </div>
  )
}

function ConferencesPanel({
  requestedSeason,
  autoRandomize = false,
  onAutoRandomizeConsumed,
}: {
  requestedSeason?: number
  autoRandomize?: boolean
  onAutoRandomizeConsumed?: () => void
}) {
  const seasons = useAsync(fetchSeasons, [])
  const upcoming = useAsync(fetchSignupStatus, [])
  const [seasonNumber, setSeasonNumber] = useState<number | null>(requestedSeason ?? null)
  const [assignments, setAssignments] = useState<Record<number, Conference | ''>>({})
  const [needsManualIds, setNeedsManualIds] = useState<Set<number>>(() => new Set())
  const [manualReasons, setManualReasons] = useState<Record<number, string>>({})
  const [draftPreview, setDraftPreview] = useState<ConferenceDraftProposal | null>(null)
  const [status, setStatus] = useState<{ tone: 'success' | 'error' | 'info'; text: string } | null>(
    null
  )
  const [saving, setSaving] = useState(false)
  const [randomizing, setRandomizing] = useState(false)
  const autoRandomizeRan = useRef(false)

  const currentSeason = seasons.data?.find(season => season.isCurrent)
  const activeSeason =
    seasonNumber ??
    requestedSeason ??
    (upcoming.data?.open === true ? upcoming.data.number : currentSeason?.number) ??
    upcoming.data?.number ??
    currentSeason?.number ??
    null
  const selectedSeason = seasons.data?.find(season => season.number === activeSeason)

  const signups = useAsync(
    () => (activeSeason ? fetchSeasonSignups(activeSeason) : Promise.resolve([])),
    [activeSeason]
  )

  useEffect(() => {
    if (requestedSeason != null) {
      setSeasonNumber(requestedSeason)
    }
  }, [requestedSeason])

  // Seed the editor from who is signed up for this season, not last year's roster.
  useEffect(() => {
    const next: Record<number, Conference | ''> = {}
    for (const row of signups.data ?? []) {
      next[row.leaguePlayerId] = row.conference ?? ''
    }
    setAssignments(next)
    setNeedsManualIds(new Set())
    setManualReasons({})
  }, [signups.data])

  const counts = useMemo(() => {
    const values = Object.values(assignments)
    return {
      AFC: values.filter(value => value === 'AFC').length,
      NFC: values.filter(value => value === 'NFC').length,
    }
  }, [assignments])

  async function save(): Promise<void> {
    if (!activeSeason) return
    setSaving(true)
    setStatus(null)

    try {
      const payload = Object.entries(assignments)
        .filter(([, conference]) => conference === 'AFC' || conference === 'NFC')
        .map(([leaguePlayerId, conference]) => ({
          leaguePlayerId: Number(leaguePlayerId),
          conference: conference as Conference,
        }))

      await saveSeasonMemberships(activeSeason, payload)
      setStatus({ tone: 'success', text: `Season ${activeSeason} conferences saved.` })
      signups.reload()
      setDraftPreview(null)
    } catch (caught) {
      setStatus({ tone: 'error', text: errorMessage(caught) })
    } finally {
      setSaving(false)
    }
  }

  async function randomize(): Promise<void> {
    if (!activeSeason) return
    const pool = (signups.data ?? []).map(row => row.leaguePlayerId)
    if (pool.length < 2) {
      setStatus({
        tone: 'error',
        text: 'Need at least two players signed up for this season before assigning conferences.',
      })
      return
    }

    setRandomizing(true)
    setStatus(null)
    try {
      const draft = await proposeConferenceSplit(activeSeason, pool)
      const poolSet = new Set(pool)
      setAssignments(current => {
        const next = { ...current }
        for (const id of poolSet) next[id] = ''
        for (const row of draft.assignments) next[row.leaguePlayerId] = row.conference
        for (const row of draft.needsManual) next[row.leaguePlayerId] = ''
        return next
      })
      setNeedsManualIds(new Set(draft.needsManual.map(row => row.leaguePlayerId)))
      setManualReasons(
        Object.fromEntries(draft.needsManual.map(row => [row.leaguePlayerId, row.reason]))
      )

      const parts: string[] = []
      if (draft.assignments.length > 0) {
        parts.push(
          `Split ${draft.assignments.length} player${draft.assignments.length === 1 ? '' : 's'} ` +
            `from three-season records, pairing each tier into opposite conferences.`
        )
      }
      if (draft.championship && !draft.championship.note) {
        const pair = draft.assignments.filter(row => row.tier === 0)
        if (pair.length === 2) {
          parts.push(
            `Season ${draft.championship.previousSeason} championship: ` +
              `${pair[0].displayName} (${pair[0].conference}) and ${pair[1].displayName} (${pair[1].conference}).`
          )
        }
      }
      if (draft.championship?.note) parts.push(draft.championship.note)
      if (draft.needsManual.length > 0) {
        parts.push(
          `Assign by hand — fewer than three seasons on file: ` +
            draft.needsManual.map(row => row.displayName).join(', ') +
            '.'
        )
      }
      if (parts.length === 0) {
        parts.push('Nothing to split. Add signups first.')
      } else if (draft.assignments.length > 0) {
        parts.push('Not saved yet — review the split and save when it looks right.')
      }

      setDraftPreview(draft)
      setStatus({
        tone: draft.needsManual.length > 0 ? 'info' : 'success',
        text: parts.join(' '),
      })
    } catch (caught) {
      setStatus({ tone: 'error', text: errorMessage(caught) })
    } finally {
      setRandomizing(false)
    }
  }

  useEffect(() => {
    if (!autoRandomize || autoRandomizeRan.current) return
    if (!activeSeason || signups.loading) return
    autoRandomizeRan.current = true
    void randomize().finally(() => onAutoRandomizeConsumed?.())
  }, [autoRandomize, activeSeason, signups.loading])

  useEffect(() => {
    if (!draftPreview) return
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') setDraftPreview(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [draftPreview])

  async function promoteSeason(): Promise<void> {
    if (!activeSeason) return
    try {
      await makeSeasonCurrent(activeSeason)
      seasons.reload()
      setStatus({ tone: 'success', text: `Season ${activeSeason} is now the current season.` })
    } catch (caught) {
      setStatus({ tone: 'error', text: errorMessage(caught) })
    }
  }

  async function changeSeasonStatus(next: SeasonStatus): Promise<void> {
    if (!activeSeason) return
    try {
      await setSeasonStatus(activeSeason, next)
      seasons.reload()
      setStatus({
        tone: 'success',
        text: `Season ${activeSeason} is now ${formatSeasonStatus(next).toLowerCase()}.`,
      })
    } catch (caught) {
      setStatus({ tone: 'error', text: errorMessage(caught) })
    }
  }

  const pool = [...(signups.data ?? [])].sort((a, b) =>
    a.displayName.localeCompare(b.displayName)
  )
  const seasonOptions = useMemo(() => {
    const numbers = new Set((seasons.data ?? []).map(season => season.number))
    if (upcoming.data?.number != null) numbers.add(upcoming.data.number)
    return [...numbers].sort((a, b) => b - a)
  }, [seasons.data, upcoming.data])
  const upcomingNumber = upcoming.data?.number ?? null

  function seasonLabel(number: number): string {
    const season = seasons.data?.find(row => row.number === number)
    const bits = [season?.name ?? `Season ${number}`]
    if (season?.isCurrent) bits.push('current')
    if (upcoming.data?.open && number === upcomingNumber) bits.push('signups open')
    return bits.length > 1 ? `${bits[0]} · ${bits.slice(1).join(' · ')}` : bits[0]
  }

  function SeasonPickerSelect() {
    return (
      <select
        className="select"
        style={{ minWidth: 160 }}
        value={activeSeason ?? ''}
        onChange={event => setSeasonNumber(Number(event.target.value))}
      >
        {seasonOptions.map(number => (
          <option key={number} value={number}>
            {seasonLabel(number)}
          </option>
        ))}
      </select>
    )
  }

  return (
    <div className="stack">
      {status && <Notice tone={status.tone}>{status.text}</Notice>}
      {draftPreview && (
        <ConferenceSplitModal
          seasonNumber={activeSeason}
          draft={draftPreview}
          saving={saving}
          onClose={() => setDraftPreview(null)}
          onSave={() => void save()}
        />
      )}

      <Card
        title="Season lifecycle"
        subtitle="Preseason while rosters are still forming, active once the schedule is under way, postseason for the playoffs, complete when the season is in the books"
        actions={<SeasonPickerSelect />}
      >
        {selectedSeason ? (
          <div className="stack">
            <div className="row">
              <Segmented
                options={SEASON_STATUS_OPTIONS}
                value={selectedSeason.status}
                onChange={next => void changeSeasonStatus(next)}
              />
              <span className="muted">
                {selectedSeason.name}
                {selectedSeason.isCurrent ? ' — current season for live uploads' : ''}
              </span>
            </div>

            <SeasonDeadlineForm
              season={selectedSeason}
              onSaved={text => {
                seasons.reload()
                setStatus({ tone: 'success', text })
              }}
              onError={text => setStatus({ tone: 'error', text })}
            />
          </div>
        ) : (
          <EmptyState title="Loading seasons…" />
        )}
      </Card>

      <Card
        title="Conference assignment"
        subtitle={`AFC ${counts.AFC} · NFC ${counts.NFC} — only players signed up for this season. Randomize splits last season's finalists and balances the rest by three-season record.`}
        actions={
          <div className="row">
            <SeasonPickerSelect />
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => void promoteSeason()}>
              Make current
            </button>
            <button
              type="button"
              className="btn btn--sm"
              disabled={saving || randomizing || pool.length < 2}
              onClick={() => void randomize()}
            >
              {randomizing ? 'Randomizing…' : 'Randomize conferences'}
            </button>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={saving}
              onClick={() => void save()}
            >
              {saving ? 'Saving…' : 'Save assignments'}
            </button>
          </div>
        }
        flush
      >
        {signups.loading ? (
          <div style={{ padding: 24 }}>
            <Loading rows={5} />
          </div>
        ) : pool.length === 0 ? (
          <EmptyState title="Nobody is signed up for this season">
            Add players on the Season signups tab first. Conference assignment only uses that pool,
            not last season&rsquo;s roster.
          </EmptyState>
        ) : (
          /*
           * Not sortable. This is an unsaved editing surface: the Conference
           * cell is a set of toggles holding pending changes, and sorting by
           * the column being edited would reshuffle the list on every click.
           * The signup list is already alphabetised above.
           */
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Conference</th>
                </tr>
              </thead>
              <tbody>
                {pool.map(player => (
                  <tr key={player.leaguePlayerId}>
                    <td>
                      <PlayerCell
                        name={player.displayName}
                        discordId={player.discordId}
                        avatar={player.discordAvatar}
                      />
                      {needsManualIds.has(player.leaguePlayerId) &&
                        manualReasons[player.leaguePlayerId] && (
                          <div className="muted" style={{ fontSize: '0.8rem', marginTop: 4 }}>
                            {manualReasons[player.leaguePlayerId]}
                          </div>
                        )}
                    </td>
                    <td>
                      <div className="segmented">
                        {(['AFC', 'NFC', ''] as const).map(option => (
                          <button
                            key={option || 'none'}
                            type="button"
                            className={option === 'AFC' ? 'is-afc' : option === 'NFC' ? 'is-nfc' : ''}
                            aria-pressed={(assignments[player.leaguePlayerId] ?? '') === option}
                            onClick={() => {
                              setAssignments(previous => ({
                                ...previous,
                                [player.leaguePlayerId]: option,
                              }))
                              if (option === 'AFC' || option === 'NFC') {
                                setNeedsManualIds(previous => {
                                  const next = new Set(previous)
                                  next.delete(player.leaguePlayerId)
                                  return next
                                })
                              }
                            }}
                          >
                            {option || 'None'}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

// --- Games -----------------------------------------------------------------

const ADMIN_GAME_COLUMNS: Array<SortableColumn<GameSummary>> = [
  { key: 'date', label: 'Date', sort: game => dateSortValue(game.playedAt ?? game.uploadedAt) },
  { key: 'season', label: 'S', className: 'num', sortLabel: 'season', sort: game => game.seasonNumber },
  {
    key: 'matchup',
    label: 'Matchup',
    hint: 'Sort by the home player',
    sort: game => game.home.displayName ?? game.home.teamAbbreviation,
  },
  { key: 'status', label: 'Status', sort: game => game.status },
  { key: 'actions' },
]

function AdminGamesTable({
  games,
  onChangeStatus,
  onEdit,
  onDownload,
  onReparse,
}: {
  games: GameSummary[]
  onChangeStatus: (gameId: number, status: 'pending' | 'official' | 'void') => void
  onEdit: (gameId: number) => void
  onDownload: (gameId: number, kind: 'primary' | '2ot') => void
  onReparse: (gameId: number) => void
}) {
  const sort = useTableSort(games, ADMIN_GAME_COLUMNS)

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <SortableHeaderRow columns={ADMIN_GAME_COLUMNS} sort={sort} />
        </thead>
        <tbody>
          {sort.rows.map(game => (
            <tr key={game.id}>
              <td className="muted">{formatDate(game.playedAt ?? game.uploadedAt)}</td>
              <td className="num">{game.seasonNumber}</td>
              <td>
                {game.home.displayName ?? game.home.teamAbbreviation ?? '—'}{' '}
                <strong>
                  {game.home.score}–{game.away.score}
                </strong>{' '}
                {game.away.displayName ?? game.away.teamAbbreviation ?? '—'}
              </td>
              <td>
                <span className="badge-stack">
                  <span
                    className={
                      game.status === 'official'
                        ? 'badge badge--win'
                        : game.status === 'void'
                          ? 'badge badge--loss'
                          : 'badge badge--pending'
                    }
                  >
                    {game.status}
                  </span>
                  <PlayoffBadge isPlayoff={game.isPlayoff} compact />
                </span>
              </td>
              <td className="num">
                <div className="row" style={{ justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn--sm" onClick={() => onEdit(game.id)}>
                    Edit
                  </button>
                  {/* Only a save-state game has a file to hand back. */}
                  {game.source === 'state_upload' && (
                    <button
                      type="button"
                      className="btn btn--sm"
                      title="Download the uploaded save state"
                      onClick={() => onDownload(game.id, 'primary')}
                    >
                      Download
                    </button>
                  )}
                  {game.hasSecondSaveState && (
                    <button
                      type="button"
                      className="btn btn--sm"
                      title="Download the second overtime save"
                      onClick={() => onDownload(game.id, '2ot')}
                    >
                      Download 2OT
                    </button>
                  )}
                  {/* Only a save-state game has a file to rebuild from. */}
                  {game.source === 'state_upload' && (
                    <button
                      type="button"
                      className="btn btn--sm"
                      title="Rebuild this box score with the current parser"
                      onClick={() => onReparse(game.id)}
                    >
                      Re-parse
                    </button>
                  )}
                  {game.status !== 'void' ? (
                    <button
                      type="button"
                      className="btn btn--danger btn--sm"
                      onClick={() => onChangeStatus(game.id, 'void')}
                    >
                      Void
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn--sm"
                      onClick={() => onChangeStatus(game.id, 'official')}
                    >
                      Restore
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const UPLOAD_COLUMNS: Array<SortableColumn<UploadLogEntry>> = [
  { key: 'file', label: 'File', sort: entry => entry.filename },
  { key: 'by', label: 'By', sortLabel: 'uploader', sort: entry => entry.uploadedBy },
  {
    key: 'status',
    label: 'Status',
    hint: 'Sort failed submissions together',
    sort: entry => entry.status,
  },
  { key: 'when', label: 'When', sort: entry => dateSortValue(entry.createdAt) },
]

function UploadLogTable({ entries }: { entries: UploadLogEntry[] }) {
  const sort = useTableSort(entries, UPLOAD_COLUMNS)

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <SortableHeaderRow columns={UPLOAD_COLUMNS} sort={sort} />
        </thead>
        <tbody>
          {sort.rows.map(entry => (
            <tr key={entry.id}>
              <td className="muted">{entry.filename}</td>
              <td>{entry.uploadedBy ?? '—'}</td>
              <td>
                <span
                  className={
                    entry.status === 'processed' ? 'badge badge--win' : 'badge badge--loss'
                  }
                  title={entry.errorMessage ?? undefined}
                >
                  {entry.status}
                </span>
              </td>
              <td className="muted">{formatDate(entry.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Both sides of a result as the admin has typed them so far. */
interface ResultDraft {
  homePlayerId: string
  awayPlayerId: string
  homeTeam: string
  awayTeam: string
  homeScore: string
  awayScore: string
  notes: string
  /** Keeps the game out of the standings and badges it wherever it is listed. */
  isPlayoff: boolean
  /** `YYYY-MM-DD` for the date input; blank leaves the stored date alone. */
  playedAt: string
}

const EMPTY_DRAFT: ResultDraft = {
  homePlayerId: '',
  awayPlayerId: '',
  homeTeam: '',
  awayTeam: '',
  homeScore: '',
  awayScore: '',
  notes: '',
  isPlayoff: false,
  playedAt: '',
}

function draftFromGame(game: GameSummary): ResultDraft {
  return {
    homePlayerId: game.home.leaguePlayerId ? String(game.home.leaguePlayerId) : '',
    awayPlayerId: game.away.leaguePlayerId ? String(game.away.leaguePlayerId) : '',
    homeTeam: game.home.teamAbbreviation ?? '',
    awayTeam: game.away.teamAbbreviation ?? '',
    homeScore: String(game.home.score),
    awayScore: String(game.away.score),
    notes: '',
    isPlayoff: game.isPlayoff,
    playedAt: game.playedAt?.slice(0, 10) ?? '',
  }
}

/**
 * Turns a draft into an API payload.
 *
 * @throws Error with a message meant for the admin when a field is unusable.
 */
function draftToPayload(draft: ResultDraft): GameResultPayload {
  const homeLeaguePlayerId = Number(draft.homePlayerId)
  const awayLeaguePlayerId = Number(draft.awayPlayerId)
  if (!homeLeaguePlayerId || !awayLeaguePlayerId) {
    throw new Error('Pick a player for both sides.')
  }
  const homeScore = Number(draft.homeScore)
  const awayScore = Number(draft.awayScore)
  if (!Number.isInteger(homeScore) || !Number.isInteger(awayScore)) {
    throw new Error('Both scores have to be whole numbers.')
  }

  return {
    homeLeaguePlayerId,
    awayLeaguePlayerId,
    homeTeamAbbreviation: draft.homeTeam || null,
    awayTeamAbbreviation: draft.awayTeam || null,
    homeScore,
    awayScore,
    notes: draft.notes.trim() || null,
    isPlayoff: draft.isPlayoff,
    // Null rather than the empty string: the server reads it as "leave the
    // date alone", which is what a blank field has to mean on a correction.
    playedAt: draft.playedAt || null,
  }
}

interface ResultFieldsProps {
  draft: ResultDraft
  players: LeaguePlayer[]
  onChange: (patch: Partial<ResultDraft>) => void
  /**
   * Locks the playoff flag on: the season is in postseason, so every recorded
   * result is a playoff game whether the admin ticks the box or not.
   */
  playoffForced?: boolean
}

/** The date, player, team, and score inputs shared by the correct and add forms. */
function ResultFields({ draft, players, onChange, playoffForced = false }: ResultFieldsProps) {
  const sides = [
    { slot: 'home' as const, label: 'P1 (home)' },
    { slot: 'away' as const, label: 'P2 (away)' },
  ]

  return (
    <>
      <label className="field">
        <span className="field__label">Date played</span>
        <input
          className="input"
          type="date"
          value={draft.playedAt}
          onChange={event => onChange({ playedAt: event.target.value })}
        />
        <span className="field__hint">Orders the game everywhere it is listed</span>
      </label>

      {sides.map(({ slot, label }) => {
        const playerKey = slot === 'home' ? 'homePlayerId' : 'awayPlayerId'
        const teamKey = slot === 'home' ? 'homeTeam' : 'awayTeam'
        const scoreKey = slot === 'home' ? 'homeScore' : 'awayScore'

        return (
          <div className="game-form__side" key={slot}>
            <label className="field">
              <span className="field__label">{label}</span>
              <select
                className="select"
                value={draft[playerKey]}
                onChange={event => onChange({ [playerKey]: event.target.value })}
              >
                <option value="">Select…</option>
                {players.map(player => (
                  <option key={player.id} value={player.id}>
                    {player.displayName}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span className="field__label">Team</span>
              <TeamSelect
                value={draft[teamKey]}
                onChange={next => onChange({ [teamKey]: next })}
              />
            </label>

            <label className="field">
              <span className="field__label">Score</span>
              <input
                className="input"
                type="number"
                min={0}
                max={200}
                value={draft[scoreKey]}
                onChange={event => onChange({ [scoreKey]: event.target.value })}
              />
            </label>
          </div>
        )
      })}

      {/* The one way to mark a game the importer never flagged, and the reason
          a hand-entered bracket game can exist as a real row without polluting
          the table it was played after. */}
      <label className="field admin-form__wide">
        <span className="row" style={{ gap: 8 }}>
          <input
            type="checkbox"
            checked={playoffForced || draft.isPlayoff}
            disabled={playoffForced}
            onChange={event => onChange({ isPlayoff: event.target.checked })}
          />
          <span>
            {playoffForced
              ? 'Playoff game — this season is in postseason, so every recorded result is one'
              : 'Playoff game — excluded from standings, badged wherever it is listed'}
          </span>
        </span>
      </label>
    </>
  )
}

interface CorrectGameCardProps {
  game: GameSummary
  players: LeaguePlayer[]
  onSaved: () => void
  onCancel: () => void
}

function CorrectGameCard({ game, players, onSaved, onCancel }: CorrectGameCardProps) {
  const [draft, setDraft] = useState<ResultDraft>(() => draftFromGame(game))
  const [status, setStatus] = useState<{ tone: 'error' | 'info'; text: string } | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setDraft(draftFromGame(game))
    setStatus(null)
  }, [game])

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    setStatus(null)
    setSaving(true)
    try {
      const saved = await updateGameResult(game.id, draftToPayload(draft))
      if (!saved.conference) {
        setStatus({
          tone: 'info',
          text:
            'Saved, but these two do not share a conference in this season, so the game will ' +
            'not count toward the round robin.',
        })
      }
      onSaved()
    } catch (caught) {
      setStatus({ tone: 'error', text: errorMessage(caught) })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card
      title={`Correct game #${game.id}`}
      subtitle={`Season ${game.seasonNumber} · ${game.status}`}
    >
      <div className="stack">
        {status && <Notice tone={status.tone}>{status.text}</Notice>}
        {game.source === 'state_upload' && (
          <Notice tone="info">
            This game came from a save state. Editing the score here does not rewrite the parsed
            box score, so the quarter-by-quarter detail will keep showing what the file reported.
          </Notice>
        )}

        <form className="admin-form game-form" onSubmit={event => void submit(event)}>
          <ResultFields
            draft={draft}
            players={players}
            onChange={patch => setDraft(current => ({ ...current, ...patch }))}
          />

          <label className="field admin-form__wide">
            <span className="field__label">Game recap or comments</span>
            <textarea
              className="input"
              rows={2}
              maxLength={2000}
              value={draft.notes}
              placeholder="Leave blank to keep the existing recap"
              onChange={event => setDraft(current => ({ ...current, notes: event.target.value }))}
            />
          </label>

          <div className="admin-form__wide row">
            <button type="submit" className="btn btn--primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save correction'}
            </button>
            <button type="button" className="btn" onClick={onCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </Card>
  )
}

function AddGameCard({ players, onSaved }: { players: LeaguePlayer[]; onSaved: () => void }) {
  const seasons = useAsync(fetchSeasons, [])
  const [seasonNumber, setSeasonNumber] = useState('')
  const [draft, setDraft] = useState<ResultDraft>(EMPTY_DRAFT)
  const [status, setStatus] = useState<{
    tone: 'success' | 'error' | 'info'
    text: string
  } | null>(null)
  const [saving, setSaving] = useState(false)

  // Default to the season the league is actually playing.
  useEffect(() => {
    if (seasonNumber || !seasons.data) return
    const current = seasons.data.find(season => season.isCurrent) ?? seasons.data[0]
    if (current) setSeasonNumber(String(current.number))
  }, [seasons.data, seasonNumber])

  const selectedSeason = seasons.data?.find(season => String(season.number) === seasonNumber)
  const playoffForced = selectedSeason ? seasonRecordsPlayoffs(selectedSeason.status) : false

  useEffect(() => {
    if (!playoffForced) return
    setDraft(current => (current.isPlayoff ? current : { ...current, isPlayoff: true }))
  }, [playoffForced])

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    setStatus(null)
    setSaving(true)
    try {
      if (!seasonNumber) throw new Error('Pick a season.')
      const saved = await createManualGame({
        ...draftToPayload(draft),
        seasonNumber: Number(seasonNumber),
        isPlayoff: playoffForced || draft.isPlayoff,
      })
      const winner = saved.home.result === 'win' ? saved.home : saved.away
      setStatus(
        saved.conference
          ? { tone: 'success', text: `Recorded game #${saved.id}. ${winner.displayName} wins.` }
          : {
              tone: 'info',
              text:
                `Recorded game #${saved.id}, but these two do not share a conference in season ` +
                `${saved.seasonNumber}, so it will not count toward the round robin.`,
            }
      )
      setDraft({ ...EMPTY_DRAFT, isPlayoff: playoffForced })
      onSaved()
    } catch (caught) {
      setStatus({ tone: 'error', text: errorMessage(caught) })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card title="Add a game result" subtitle="For a game played without a save state">
      <div className="stack">
        {status && <Notice tone={status.tone}>{status.text}</Notice>}

        <form className="admin-form game-form" onSubmit={event => void submit(event)}>
          <label className="field">
            <span className="field__label">Season</span>
            <select
              className="select"
              value={seasonNumber}
              onChange={event => setSeasonNumber(event.target.value)}
            >
              <option value="">Select…</option>
              {seasons.data?.map(season => (
                <option key={season.number} value={season.number}>
                  {season.name}
                </option>
              ))}
            </select>
          </label>

          <ResultFields
            draft={draft}
            players={players}
            playoffForced={playoffForced}
            onChange={patch => setDraft(current => ({ ...current, ...patch }))}
          />

          <label className="field admin-form__wide">
            <span className="field__label">Game recap or comments</span>
            <textarea
              className="input"
              rows={2}
              maxLength={2000}
              value={draft.notes}
              placeholder="Optional — shown on the game page"
              onChange={event => setDraft(current => ({ ...current, notes: event.target.value }))}
            />
          </label>

          <div className="admin-form__wide">
            <button type="submit" className="btn btn--primary" disabled={saving}>
              {saving ? 'Recording…' : 'Record result'}
            </button>
          </div>
        </form>
      </div>
    </Card>
  )
}

/** Human-readable size for the archive tile; files are ~13 KB each. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Per-game detail for a re-parse, listing the names that moved.
 *
 * @remarks
 * Skips outcomes with nothing to say, so a clean run over hundreds of files
 * renders as the summary line alone rather than hundreds of empty rows.
 */
function ReparseReport({ outcomes }: { outcomes: ReparseOutcome[] }) {
  const worthShowing = outcomes.filter(
    outcome =>
      outcome.error !== null ||
      outcome.scoreMismatch !== null ||
      outcome.nameChanges.length > 0 ||
      outcome.status === 'updated'
  )
  if (worthShowing.length === 0) return null

  return (
    <ul className="stack" style={{ margin: 0, paddingLeft: 18, fontSize: '0.85rem' }}>
      {worthShowing.map(outcome => (
        <li key={outcome.gameId}>
          <strong>
            #{outcome.gameId} (S{outcome.seasonNumber})
          </strong>{' '}
          {outcome.error ? (
            <span className="muted">{outcome.error}</span>
          ) : (
            <span className="muted">
              {outcome.nameChanges.length > 0
                ? outcome.nameChanges
                    .map(
                      change =>
                        `${change.slot} ${change.from ?? 'unnamed'} → ${change.to ?? 'unnamed'}`
                    )
                    .join(', ')
                : 'box score refreshed'}
            </span>
          )}
          {outcome.scoreMismatch && (
            <div style={{ color: 'var(--brand-red)' }}>{outcome.scoreMismatch}</div>
          )}
        </li>
      ))}
    </ul>
  )
}

/**
 * The retained save-state archive, and the button that replays the parser over it.
 *
 * @remarks
 * Every box score on the site is derived from a file, so a parser fix is only
 * half a fix until it has been replayed here. Games uploaded before retention
 * shipped have no file and are reported as `missing` — permanently, since the
 * originals are gone.
 */
function SaveStateCard({ onReparsed }: { onReparsed: () => void }) {
  const inventory = useAsync(fetchSaveStateInventory, [])
  const [running, setRunning] = useState<'reparse' | 'resync' | null>(null)
  const [summary, setSummary] = useState<ReparseSummary | null>(null)
  const [resync, setResync] = useState<ResyncSummary | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function runAll(): Promise<void> {
    setRunning('reparse')
    setError(null)
    setSummary(null)
    setResync(null)
    try {
      setSummary(await reparseAllSaveStates())
      inventory.reload()
      onReparsed()
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setRunning(null)
    }
  }

  async function runResync(): Promise<void> {
    setRunning('resync')
    setError(null)
    setSummary(null)
    setResync(null)
    try {
      setResync(await resyncRosterNames())
      onReparsed()
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setRunning(null)
    }
  }

  const data = inventory.data

  return (
    <Card
      title="Save state archive"
      subtitle="Retained uploads, and box scores rebuilt from them"
    >
      <div className="stack">
        {error && <Notice tone="error">{error}</Notice>}

        {data && (
          <div className="stat-grid">
            <Stat
              label="Retained"
              value={`${data.retained} of ${data.stateGames}`}
              hint="Save-state games whose file is still on hand"
            />
            <Stat
              label="Files held"
              value={String(data.filesOnDisk)}
              hint={formatBytes(data.bytesOnDisk)}
            />
          </div>
        )}

        {data && data.retained < data.stateGames && (
          <Notice>
            {data.stateGames - data.retained} games were uploaded before retention
            shipped, so their files are gone and their box scores cannot be rebuilt.
          </Notice>
        )}

        <p className="muted" style={{ margin: 0, fontSize: '0.85rem' }}>
          <strong>Re-parse</strong> rebuilds box scores from the retained files, for
          after a parser fix ships. <strong>Resync names</strong> only relabels the
          players on stored box scores from the roster table, which is what to use for
          games whose file is gone. Neither touches scores or player assignments; any
          disagreement is reported for you to correct by hand.
        </p>

        <div className="row">
          <button
            type="button"
            className="btn btn--primary"
            disabled={running !== null}
            onClick={() => void runAll()}
          >
            {running === 'reparse' ? 'Re-parsing…' : 'Re-parse every save state'}
          </button>
          <button
            type="button"
            className="btn"
            disabled={running !== null}
            onClick={() => void runResync()}
          >
            {running === 'resync' ? 'Resyncing…' : 'Resync names from roster'}
          </button>
        </div>

        {summary && (
          <>
            <Notice tone={summary.failed > 0 ? 'error' : 'success'}>
              Scanned {summary.scanned}: {summary.updated} updated, {summary.unchanged}{' '}
              unchanged, {summary.missing} without a file, {summary.failed} failed.
            </Notice>
            <ReparseReport outcomes={summary.outcomes} />
          </>
        )}

        {resync && (
          <>
            <Notice tone="success">
              Scanned {resync.scanned} box scores: {resync.updated} updated,{' '}
              {resync.renamed} names corrected, {resync.filled} filled in.
              {resync.byTeam.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  {resync.byTeam.map(entry => `${entry.team} ${entry.slots}`).join(' · ')}
                </div>
              )}
            </Notice>
            {resync.examples.length > 0 && (
              <ul
                className="stack"
                style={{ margin: 0, paddingLeft: 18, fontSize: '0.85rem' }}
              >
                {resync.examples.map(example => (
                  <li key={example} className="muted">
                    {example}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </Card>
  )
}

function GamesPanel() {
  const games = useAsync(() => fetchAdminGames({ season: 'all', limit: 200 }), [])
  const players = useAsync(fetchLeaguePlayers, [])
  const uploads = useAsync(fetchUploadLog, [])
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [reparsed, setReparsed] = useState<ReparseOutcome | null>(null)

  const editing = games.data?.find(game => game.id === editingId) ?? null

  async function runReparse(gameId: number): Promise<void> {
    setError(null)
    setReparsed(null)
    try {
      const outcome = await reparseGame(gameId)
      setReparsed(outcome)
      if (outcome.status === 'updated') games.reload()
    } catch (caught) {
      setError(errorMessage(caught))
    }
  }

  async function runDownload(gameId: number, kind: 'primary' | '2ot'): Promise<void> {
    setError(null)
    try {
      await downloadGameSaveState(gameId, kind)
    } catch (caught) {
      setError(errorMessage(caught, 'The save state could not be downloaded.'))
    }
  }

  async function changeStatus(
    gameId: number,
    status: 'pending' | 'official' | 'void'
  ): Promise<void> {
    setError(null)
    try {
      await setGameStatus(gameId, status)
      games.reload()
    } catch (caught) {
      setError(errorMessage(caught))
    }
  }

  return (
    <div className="stack">
      {error && <Notice tone="error">{error}</Notice>}

      {editing && (
        <CorrectGameCard
          game={editing}
          players={players.data ?? []}
          onSaved={() => {
            games.reload()
            setEditingId(null)
          }}
          onCancel={() => setEditingId(null)}
        />
      )}

      <AddGameCard players={players.data ?? []} onSaved={() => games.reload()} />

      <SaveStateCard onReparsed={() => games.reload()} />

      {reparsed && (
        <Notice
          tone={
            reparsed.status === 'failed'
              ? 'error'
              : reparsed.status === 'updated'
                ? 'success'
                : 'info'
          }
        >
          {reparsed.status === 'missing'
            ? `Game #${reparsed.gameId} has no retained save state, so there is nothing to rebuild from.`
            : reparsed.status === 'failed'
              ? `Game #${reparsed.gameId} could not be re-parsed: ${reparsed.error}`
              : reparsed.status === 'unchanged'
                ? `Game #${reparsed.gameId} re-parsed with no changes.`
                : `Game #${reparsed.gameId} updated.`}
          <ReparseReport outcomes={[reparsed]} />
        </Notice>
      )}

      <Card
        title="Recent games"
        subtitle="Correct a result, download a save, or void a disputed one. Voided games stay here."
        flush
      >
        {games.loading ? (
          <div style={{ padding: 24 }}>
            <Loading rows={5} />
          </div>
        ) : (
          <AdminGamesTable
            games={games.data ?? []}
            onChangeStatus={(gameId, status) => void changeStatus(gameId, status)}
            onEdit={setEditingId}
            onDownload={(gameId, kind) => void runDownload(gameId, kind)}
            onReparse={gameId => void runReparse(gameId)}
          />
        )}
      </Card>

      <Card title="Upload log" subtitle="The 100 most recent save state submissions" flush>
        {uploads.loading ? (
          <div style={{ padding: 24 }}>
            <Loading rows={3} />
          </div>
        ) : uploads.data && uploads.data.length > 0 ? (
          <UploadLogTable entries={uploads.data} />
        ) : (
          <EmptyState title="No uploads yet" />
        )}
      </Card>
    </div>
  )
}

// --- Champions -------------------------------------------------------------

const CHAMPION_COLUMNS: Array<SortableColumn<Champion>> = [
  { key: 'season', label: 'Season', className: 'num', sort: champion => champion.seasonNumber },
  { key: 'champion', label: 'Champion', sort: champion => champion.championName },
  { key: 'team', label: 'Team', sort: champion => champion.championTeamAbbreviation },
  { key: 'runnerUp', label: 'Runner-up', sort: champion => champion.runnerUpName },
  { key: 'series', label: 'Series', sort: champion => champion.seriesResult },
  {
    key: 'decided',
    label: 'Decided',
    hint: 'Sort by the date the title was settled',
    sort: champion => dateSortValue(champion.decidedOn),
  },
  { key: 'actions' },
]

function ChampionsTable({
  champions,
  onDelete,
}: {
  champions: Champion[]
  onDelete: (id: number) => void
}) {
  const sort = useTableSort(champions, CHAMPION_COLUMNS)

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <SortableHeaderRow columns={CHAMPION_COLUMNS} sort={sort} />
        </thead>
        <tbody>
          {sort.rows.map(champion => (
            <tr key={champion.id}>
              <td className="num">{champion.seasonNumber}</td>
              <td>
                <div className="player">
                  <Avatar
                    name={champion.championName}
                    discordId={champion.championDiscordId}
                    avatar={champion.championAvatar}
                  />
                  <span className="player__name">{champion.championName}</span>
                </div>
              </td>
              <td>{champion.championTeamAbbreviation ?? '—'}</td>
              <td className="muted">{champion.runnerUpName ?? '—'}</td>
              <td className="num">{champion.seriesResult ?? '—'}</td>
              <td className="muted">{formatCalendarDate(champion.decidedOn)}</td>
              <td className="num">
                <button
                  type="button"
                  className="btn btn--danger btn--sm"
                  onClick={() => onDelete(champion.id)}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ChampionsPanel() {
  const champions = useAsync(fetchChampions, [])
  const seasons = useAsync(fetchSeasons, [])
  const players = useAsync(fetchLeaguePlayers, [])

  const [seasonNumber, setSeasonNumber] = useState('')
  const [championLeaguePlayerId, setChampionLeaguePlayerId] = useState('')
  const [championName, setChampionName] = useState('')
  const [team, setTeam] = useState('')
  const [runnerUpLeaguePlayerId, setRunnerUpLeaguePlayerId] = useState('')
  const [runnerUp, setRunnerUp] = useState('')
  const [decidedOn, setDecidedOn] = useState('')
  const [seriesResult, setSeriesResult] = useState('')
  const [blurb, setBlurb] = useState('')
  const [status, setStatus] = useState<{ tone: 'success' | 'error'; text: string } | null>(null)

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    setStatus(null)

    const resolvedPlayer = players.data?.find(
      player => String(player.id) === championLeaguePlayerId
    )
    const resolvedName = championName.trim() || resolvedPlayer?.displayName || ''

    if (!seasonNumber || !resolvedName) {
      setStatus({ tone: 'error', text: 'A season and a champion name are required.' })
      return
    }

    // A runner-up who never had an account is entered as a bare name, so the
    // typed override wins over the (optional) linked player.
    const resolvedRunnerUpPlayer = players.data?.find(
      player => String(player.id) === runnerUpLeaguePlayerId
    )
    const resolvedRunnerUpName = runnerUp.trim() || resolvedRunnerUpPlayer?.displayName || null

    if (seriesResult.trim() && !/^\d+-\d+$/.test(seriesResult.trim())) {
      setStatus({ tone: 'error', text: 'Series result must look like 2-1.' })
      return
    }

    try {
      await saveChampion({
        seasonNumber: Number(seasonNumber),
        championName: resolvedName,
        championLeaguePlayerId: resolvedPlayer?.id ?? null,
        championTeamAbbreviation: team || null,
        runnerUpName: resolvedRunnerUpName,
        runnerUpLeaguePlayerId: resolvedRunnerUpPlayer?.id ?? null,
        decidedOn: decidedOn || null,
        seriesResult: seriesResult.trim() || null,
        blurb: blurb.trim() || null,
      })
      setStatus({ tone: 'success', text: `Season ${seasonNumber} champion saved.` })
      setChampionName('')
      setRunnerUp('')
      setDecidedOn('')
      setSeriesResult('')
      setBlurb('')
      champions.reload()
    } catch (caught) {
      setStatus({ tone: 'error', text: errorMessage(caught) })
    }
  }

  async function remove(id: number): Promise<void> {
    try {
      await deleteChampion(id)
      champions.reload()
    } catch (caught) {
      setStatus({ tone: 'error', text: errorMessage(caught) })
    }
  }

  return (
    <div className="stack">
      {status && <Notice tone={status.tone}>{status.text}</Notice>}

      <Card
        title="Add or update a champion"
        subtitle="Finished brackets write this on their own. Use this form for historical seasons and blurbs."
      >
        <form className="admin-form" onSubmit={submit}>
          <label className="field">
            <span className="field__label">Season</span>
            <select
              className="select"
              value={seasonNumber}
              onChange={event => setSeasonNumber(event.target.value)}
            >
              <option value="">Select…</option>
              {seasons.data?.map(season => (
                <option key={season.number} value={season.number}>
                  {season.name}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">Champion (existing player)</span>
            <select
              className="select"
              value={championLeaguePlayerId}
              onChange={event => setChampionLeaguePlayerId(event.target.value)}
            >
              <option value="">Not in the league</option>
              {players.data?.map(player => (
                <option key={player.id} value={player.id}>
                  {player.displayName}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">Champion name override</span>
            <input
              className="input"
              value={championName}
              placeholder="Leave blank to use the selected player"
              onChange={event => setChampionName(event.target.value)}
            />
          </label>

          <label className="field">
            <span className="field__label">Winning team</span>
            <TeamSelect value={team} onChange={setTeam} />
          </label>

          <label className="field">
            <span className="field__label">Runner-up (existing player)</span>
            <select
              className="select"
              value={runnerUpLeaguePlayerId}
              onChange={event => setRunnerUpLeaguePlayerId(event.target.value)}
            >
              <option value="">Not in the league</option>
              {players.data?.map(player => (
                <option key={player.id} value={player.id}>
                  {player.displayName}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">Runner-up name override</span>
            <input
              className="input"
              value={runnerUp}
              placeholder="Leave blank to use the selected player"
              onChange={event => setRunnerUp(event.target.value)}
            />
          </label>

          <label className="field">
            <span className="field__label">Date decided</span>
            <input
              className="input"
              type="date"
              value={decidedOn}
              onChange={event => setDecidedOn(event.target.value)}
            />
          </label>

          <label className="field">
            <span className="field__label">Series result</span>
            <input
              className="input"
              value={seriesResult}
              placeholder="e.g. 2-1"
              onChange={event => setSeriesResult(event.target.value)}
            />
          </label>

          <label className="field admin-form__wide">
            <span className="field__label">Notes</span>
            <textarea
              className="input"
              rows={2}
              value={blurb}
              onChange={event => setBlurb(event.target.value)}
            />
          </label>

          <div className="admin-form__wide">
            <button type="submit" className="btn btn--primary">
              Save champion
            </button>
          </div>
        </form>
      </Card>

      <Card title="Recorded champions" flush>
        {champions.data && champions.data.length > 0 ? (
          <ChampionsTable
            champions={champions.data}
            onDelete={id => void remove(id)}
          />
        ) : (
          <EmptyState title="No champions recorded yet" />
        )}
      </Card>
    </div>
  )
}

// --- Historical import -----------------------------------------------------

/** Renders one bucket of skipped rows, or nothing when the bucket is empty. */
function SkipList({
  title,
  hint,
  items,
}: {
  title: string
  hint?: string
  items: ImportSummary['skipped']
}) {
  if (!items.length) {
    return null
  }

  return (
    <div style={{ marginTop: 20 }}>
      <h3 style={{ marginBottom: 4 }}>
        {title} ({items.length})
      </h3>
      {hint && <p className="skip-list-hint">{hint}</p>}
      <ul className="skip-list">
        {items.slice(0, 25).map(item => (
          <li key={item.row}>
            Row {item.row}: {item.reason}
          </li>
        ))}
      </ul>
    </div>
  )
}

function ImportPanel() {
  const seasons = useAsync(fetchSeasons, [])
  const [seasonNumber, setSeasonNumber] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [replaceExisting, setReplaceExisting] = useState(true)
  const [running, setRunning] = useState(false)
  const [summary, setSummary] = useState<ImportSummary | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function run(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    if (!file || !seasonNumber) return

    setRunning(true)
    setError(null)
    setSummary(null)

    try {
      setSummary(await importSeasonExcel(Number(seasonNumber), file, replaceExisting))
      seasons.reload()
    } catch (caught) {
      setError(errorMessage(caught, 'The import failed.'))
    } finally {
      setRunning(false)
    }
  }

  const playoffSkips = summary?.skipped.filter(item => item.category === 'playoff') ?? []
  const errorSkips = summary?.skipped.filter(item => item.category === 'error') ?? []

  return (
    <div className="stack">
      <Notice>
        Historical import is admin-only and safe to run any time after launch. It reads the{' '}
        <code>Winner Enters Game Results Here</code> sheet (columns A–I) and takes each player's
        conference from the <code>participants</code> sheet. The P1 player is recorded as the home
        side. Playoff games — any rematch, or any cross-conference game — are skipped for now. See{' '}
        <code>docs/EXCEL_IMPORT.md</code>.
      </Notice>

      <Card title="Import a season workbook" subtitle="Seasons 1–32">
        <form className="admin-form" onSubmit={run}>
          <label className="field">
            <span className="field__label">Target season</span>
            <select
              className="select"
              value={seasonNumber}
              onChange={event => setSeasonNumber(event.target.value)}
            >
              <option value="">Select…</option>
              {seasons.data
                ?.filter(season => !season.isCurrent)
                .map(season => (
                  <option key={season.number} value={season.number}>
                    {season.name} ({season.gameCount} games)
                  </option>
                ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">Workbook</span>
            <input
              className="input"
              type="file"
              accept=".xlsx,.xlsm,.xls,.csv"
              onChange={event => setFile(event.target.files?.[0] ?? null)}
            />
          </label>

          <label className="field admin-form__wide">
            <span className="row" style={{ gap: 8 }}>
              <input
                type="checkbox"
                checked={replaceExisting}
                onChange={event => setReplaceExisting(event.target.checked)}
              />
              <span>Replace previously imported games for this season</span>
            </span>
          </label>

          <div className="admin-form__wide">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={!file || !seasonNumber || running}
            >
              {running ? 'Importing…' : 'Run import'}
            </button>
          </div>
        </form>
      </Card>

      {error && <Notice tone="error">{error}</Notice>}

      {summary && (
        <Card
          title={`Season ${summary.seasonNumber} import complete`}
          subtitle={`Sheet: ${summary.sheetName}`}
        >
          <div className="grid-3">
            <div>
              <div className="stat__label">Result rows read</div>
              <div className="stat__value">{summary.rowsRead}</div>
            </div>
            <div>
              <div className="stat__label">Games imported</div>
              <div className="stat__value">{summary.gamesImported}</div>
            </div>
            <div>
              <div className="stat__label">Players touched</div>
              <div className="stat__value">{summary.playersTouched}</div>
            </div>
            <div>
              <div className="stat__label">Playoff rows excluded</div>
              <div className="stat__value">{summary.playoffGamesSkipped}</div>
            </div>
            <div>
              <div className="stat__label">Rows with errors</div>
              <div className="stat__value">{summary.rowsWithErrors}</div>
            </div>
          </div>

          <SkipList
            title="Playoff games excluded"
            hint="Rematches and cross-conference games are left out until the site models playoffs."
            items={playoffSkips}
          />
          <SkipList title="Rows with errors" items={errorSkips} />

          {summary.warnings.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <h3 style={{ marginBottom: 8 }}>
                Imported with warnings ({summary.warnings.length})
              </h3>
              <ul className="skip-list">
                {summary.warnings.slice(0, 25).map((item, index) => (
                  <li key={`${item.row ?? 'sheet'}-${index}`}>
                    {item.row === null ? item.message : `Row ${item.row}: ${item.message}`}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}
    </div>
  )
}

// --- Page ------------------------------------------------------------------

export default function Admin() {
  const { isAdmin, loading } = useSession()
  const [tab, setTab] = useState<Tab>('users')
  const [conferenceFocus, setConferenceFocus] = useState<{
    season: number
    randomize: boolean
  } | null>(null)

  if (loading) {
    return (
      <Card title="Admin">
        <Loading rows={3} />
      </Card>
    )
  }

  if (!isAdmin) {
    return (
      <Card>
        <EmptyState title="Admins only">
          You do not have access to this section. Every admin endpoint also enforces this
          server-side.
        </EmptyState>
      </Card>
    )
  }

  return (
    <div className="stack">
      <header className="page-header">
        <div>
          <span className="eyebrow">Admin</span>
          <h1>League management</h1>
          <p>Conference assignments, result corrections, champions, and historical imports.</p>
        </div>
      </header>

      <div className="row">
        {TABS.map(item => (
          <button
            key={item.key}
            type="button"
            className={item.key === tab ? 'btn btn--accent btn--sm' : 'btn btn--sm'}
            onClick={() => setTab(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'users' && <UsersPanel />}
      {tab === 'merge' && <PlayerMergePanel />}
      {tab === 'aliases' && <PlayerAliasPanel />}
      {tab === 'signups' && (
        <SignupsPanel
          onOpenConferences={season => {
            setConferenceFocus({ season, randomize: true })
            setTab('conferences')
          }}
        />
      )}
      {tab === 'conferences' && (
        <ConferencesPanel
          requestedSeason={conferenceFocus?.season}
          autoRandomize={conferenceFocus?.randomize ?? false}
          onAutoRandomizeConsumed={() =>
            setConferenceFocus(previous => (previous ? { ...previous, randomize: false } : null))
          }
        />
      )}
      {tab === 'games' && <GamesPanel />}
      {tab === 'playoffs' && <PlayoffsPanel />}
      {tab === 'poll' && <PollPanel />}
      {tab === 'updates' && <UpdatesPanel />}
      {tab === 'champions' && <ChampionsPanel />}
      {tab === 'import' && <ImportPanel />}
    </div>
  )
}
